import cardData from "@/data/riftbound/card_data.json";
import { RBItem } from "@/interfaces/CardItem";
import { SrcType } from "@/models/alt-arts";
import { AaMap } from "@/models/alt-arts";
import aaJson from "@/data/riftbound/aa_data.json";
import { BaseCardDataManager } from "@/lib/games/base/getCardDataBase";

const aa = aaJson as AaMap;

class RiftboundCardDataManager extends BaseCardDataManager<RBItem> {
  protected readonly cdnGame = "riftbound" as const;
  protected readonly imageVersion = "1.3";
  protected readonly defaultRegion = "en";

  constructor() {
    super(aa);
  }

  public getCardNumber(cardId: string): string {
    if (cardId.endsWith("a") || cardId.endsWith("s")) {
      return cardId.slice(0, -1);
    }
    return cardId;
  }

  protected clean(data: Record<string, any>): Record<string, RBItem> {
    let cleanedData: Record<string, RBItem> = {};
    for (const [k, v] of Object.entries(data)) {
      // Create a deep copy of the current item
      let item: any = structuredClone(v);

      if (item.energy_cost) {
        item.cost = item.energy_cost;
      }

      if (item.effect === null) {
        item.description = "";
      }

      // Add the cleaned item to the cleanedData object
      cleanedData[k] = item;
    }

    return cleanedData;
  }

  public cleanCardData(): Record<string, RBItem> {
    // Check if CARD_DATA array is not empty
    if (Object.keys(this.CARD_DATA).length === 0) {
      this.CARD_DATA = this.clean(cardData);
    }
    // Return an empty array if CARD_DATA is empty
    return this.CARD_DATA;
  }

  public getCardData(cardId: string): RBItem | undefined {
    if (!cardId) {
      return undefined;
    }

    const cardNumber = cardId.split("_p")[0];
    return this.cleanCardData()[cardNumber];
  }

  public isCardNumber(cardId: string): boolean {
    // Card numbers in Riftbound are like "027a/298" or "066/298"
    // Card IDs are numeric strings like "652799"
    return !/^\d+$/.test(cardId);
  }

  public getCardId(cardId: string): string | undefined {
    if (this.isCardNumber(cardId)) {
      const cardNumber = cardId;
      if (cardNumber in this.aa && this.aa[cardNumber].length > 0) {
        // Get the first card with "en" src, or first card if no "en" src exists
        const enCard = this.aa[cardNumber].find(
          (card) => card.src.toLowerCase() === "en",
        );
        const firstCard = enCard || this.aa[cardNumber][0];
        return firstCard.id;
      }
    } else {
      return cardId;
    }
    return undefined;
  }

  public getTcgPlayerCardName(tcgplayerId: string): string {
    // Look up card by TCGPlayer ID in aa_data
    for (const [_cardNumber, cards] of Object.entries(this.aa)) {
      for (const card of cards) {
        if (card.tcgplayer_id === tcgplayerId || card.id === tcgplayerId) {
          return card.name || "";
        }
      }
    }
    return "";
  }

  public getRBIdFromTcgPlayerId(tcgplayerId: string): string | undefined {
    // Look up card by TCGPlayer ID and return the card ID
    for (const [_cardNumber, cards] of Object.entries(this.aa)) {
      for (const card of cards) {
        if (card.tcgplayer_id === tcgplayerId || card.id === tcgplayerId) {
          return card.id;
        }
      }
    }
    return undefined;
  }

  public getSetFromCardId(cardId: string): string | undefined {
    // Look up card by ID and return its set
    for (const [_cardNumber, cards] of Object.entries(this.aa)) {
      for (const card of cards) {
        if (card.id === cardId) {
          return card.set;
        }
      }
    }
    return undefined;
  }
}

// Create singleton instance
const riftboundManager = new RiftboundCardDataManager();

// Export functions that delegate to the manager
export function cleanCardData(): Record<string, RBItem> {
  return riftboundManager.cleanCardData();
}

export function getCardImage(
  cardId: string,
  size: "original" | "316x440" | "158x220" | "90x125" = "original",
  region: SrcType | "" = "",
): string {
  return riftboundManager.getCardImage(cardId, size, region);
}

export function getCardData(cardId: string): RBItem | undefined {
  return riftboundManager.getCardData(cardId);
}

export function getAACardIdData(aaCardId: string): RBItem | undefined {
  return riftboundManager.getAACardIdData(aaCardId);
}

export function getAACardId(aa: any): string {
  return riftboundManager.getAACardId(aa);
}

export function getAAFromCardId(aaCardId: string): any {
  return riftboundManager.getAAFromCardId(aaCardId);
}

export function getAA(cardNumber: string, srcChar: string, count: number): any {
  return riftboundManager.getAA(cardNumber, srcChar, count);
}

export function getAltArts(cardId: string, showAAs: boolean): any[] {
  return riftboundManager.getAltArts(cardId, showAAs);
}

export function isCardNumber(cardId: string): boolean {
  return riftboundManager.isCardNumber(cardId);
}

export function getCardId(cardId: string): string | undefined {
  return riftboundManager.getCardId(cardId);
}

export function getTcgPlayerCardName(tcgplayerId: string): string {
  return riftboundManager.getTcgPlayerCardName(tcgplayerId);
}

export function getRBIdFromTcgPlayerId(
  tcgplayerId: string,
): string | undefined {
  return riftboundManager.getRBIdFromTcgPlayerId(tcgplayerId);
}

export function getSetFromCardId(cardId: string): string | undefined {
  return riftboundManager.getSetFromCardId(cardId);
}
